import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useApi } from '../contexts/ApiContext';
import DashboardLayout from '../components/DashboardLayout';
import {
  Upload,
  FileVideo,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Clock
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const UploadVideo = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { api } = useApi();
  const [order, setOrder] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [loading, setLoading] = useState(true);

  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data.data.order);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    } finally {
      setLoading(false);
    }
  }, [api, orderId]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('Please upload a video file');
      return;
    }

    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      alert('File size must be less than 100MB');
      return;
    }

    await uploadVideo(file);
  };

  const uploadVideo = async (file) => {
    try {
      setUploadStatus('uploading');
      setUploadProgress(0);

      // Get pre-signed upload URL
      const uploadResponse = await api.post('/s3/upload-url', {
        orderId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });

      const { uploadUrl, videoKey } = uploadResponse.data.data;

      // Upload to S3
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          // Confirm upload with backend
          try {
            await api.post('/s3/confirm-upload', {
              orderId,
              videoKey,
              fileName: file.name,
              fileSize: file.size
            });

            setUploadStatus('success');
            setUploadProgress(100);
            
            // Redirect to orders page after 2 seconds
            setTimeout(() => {
              navigate('/orders');
            }, 2000);
          } catch (error) {
            console.error('Failed to confirm upload:', error);
            setUploadStatus('error');
          }
        } else {
          setUploadStatus('error');
        }
      });

      xhr.addEventListener('error', () => {
        setUploadStatus('error');
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('error');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.wmv']
    },
    maxFiles: 1,
    disabled: uploadStatus === 'uploading'
  });

  if (loading) {
    return (
      <DashboardLayout>
        <LoadingSpinner size="xl" text="Loading order details..." />
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-error-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Order not found</h3>
          <p className="text-gray-500">The order you're looking for doesn't exist.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/orders')}
              className="btn-outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Orders
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Upload Video</h1>
              <p className="text-gray-600">Upload the completed video for this order</p>
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-700">Recipient</p>
              <p className="text-lg text-gray-900">{order.recipientName}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Occasion</p>
              <p className="text-lg text-gray-900">{order.occasion}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Custom Message</p>
              <p className="text-gray-900">
                {order.customMessage || 'No custom message provided'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Order Date</p>
              <p className="text-gray-900">
                {new Date(order.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Video</h2>
          
          {uploadStatus === 'success' ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-success-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Successful!</h3>
              <p className="text-gray-500">Your video has been uploaded successfully.</p>
              <p className="text-sm text-gray-400 mt-2">Redirecting to orders page...</p>
            </div>
          ) : uploadStatus === 'error' ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-error-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Failed</h3>
              <p className="text-gray-500">There was an error uploading your video. Please try again.</p>
              <button
                onClick={() => setUploadStatus('idle')}
                className="btn-primary mt-4"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragActive
                  ? 'border-primary-400 bg-primary-50'
                  : uploadStatus === 'uploading'
                  ? 'border-gray-300 bg-gray-50'
                  : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
              }`}
            >
              <input {...getInputProps()} />
              
              {uploadStatus === 'uploading' ? (
                <div>
                  <Clock className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Uploading Video...</h3>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500">{uploadProgress}% complete</p>
                </div>
              ) : (
                <div>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {isDragActive ? 'Drop the video here' : 'Upload Video'}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Drag and drop your video file here, or click to select a file
                  </p>
                  <div className="text-sm text-gray-400 space-y-1">
                    <p>Supported formats: MP4, MOV, AVI, MKV, WMV</p>
                    <p>Maximum file size: 100MB</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Upload Guidelines */}
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-start">
            <div className="bg-blue-100 p-3 rounded-lg">
              <FileVideo className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-blue-800">
                Video Upload Guidelines
              </h3>
              <ul className="text-blue-700 mt-2 space-y-1 text-sm">
                <li>• Ensure the video is high quality and clearly shows the band performing</li>
                <li>• Include the recipient's name and custom message in the video</li>
                <li>• Keep the video length between 30 seconds and 3 minutes</li>
                <li>• Use landscape orientation for better viewing experience</li>
                <li>• The video will be automatically delivered to the customer via email</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UploadVideo; 